/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * BillingTab Component - Subscription billing management tab for Profile page
 *
 * Context-aware: displays personal or organization billing based on OrganizationSwitcher.
 * Only org admins can manage organization billing.
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect, useCallback } from 'react'
import { useOrganizationStore } from '../../stores/organizationStore'
import subscriptionService from '../../services/subscriptionService'
import LoadingSpinner from '../LoadingSpinner'
import ConfirmDialog from '../ConfirmDialog'
import toast from 'react-hot-toast'
import {
  CreditCard,
  Crown,
  Check,
  AlertTriangle,
  ExternalLink,
  Calendar,
  Users,
  Sparkles,
  ArrowRight,
  Tag,
  Info
} from 'lucide-react'
import { createLogger } from '../../utils/logger'

const logger = createLogger('BillingTab')

/**
 * Get human-readable label for billing interval
 * @param {string} interval - 'month' or 'year'
 * @param {number} intervalCount - Number of intervals
 * @returns {string} Label like "Monthly", "Quarterly", "6 Months", etc.
 */
const getIntervalLabel = (interval, intervalCount = 1) => {
  if (interval === 'year') {
    return intervalCount === 1 ? 'Annual' : `${intervalCount} Years`
  }
  if (intervalCount === 1) return 'Monthly'
  if (intervalCount === 3) return 'Quarterly'
  if (intervalCount === 6) return '6 Months'
  if (intervalCount === 10) return '10 Months'
  if (intervalCount === 12) return 'Annual'
  return `${intervalCount} Months`
}

/**
 * Get interval key for price lookup
 * @param {string} interval - 'month' or 'year'
 * @param {number} intervalCount - Number of intervals
 * @returns {string} Key like "monthly", "quarterly", "sixMonth", "annual", etc.
 */
const getIntervalKey = (interval, intervalCount = 1) => {
  if (interval === 'year') {
    return intervalCount === 1 ? 'annual' : `year_${intervalCount}`
  }
  if (intervalCount === 1) return 'monthly'
  if (intervalCount === 3) return 'quarterly'
  if (intervalCount === 6) return 'sixMonth'
  if (intervalCount === 10) return 'tenMonth'
  if (intervalCount === 12) return 'annual'
  return `month_${intervalCount}`
}

/**
 * Parse interval key to components
 * @param {string} key - e.g., "monthly", "quarterly", "sixMonth", "month_6"
 * @returns {{ interval: string, intervalCount: number }}
 */
const parseIntervalKey = (key) => {
  const mapping = {
    monthly: { interval: 'month', intervalCount: 1 },
    quarterly: { interval: 'month', intervalCount: 3 },
    sixMonth: { interval: 'month', intervalCount: 6 },
    tenMonth: { interval: 'month', intervalCount: 10 },
    annual: { interval: 'year', intervalCount: 1 }
  }
  if (mapping[key]) return mapping[key]

  // Handle dynamic keys like "month_6" or "year_2"
  if (key.startsWith('month_')) {
    return { interval: 'month', intervalCount: parseInt(key.replace('month_', ''), 10) }
  }
  if (key.startsWith('year_')) {
    return { interval: 'year', intervalCount: parseInt(key.replace('year_', ''), 10) }
  }

  return { interval: 'month', intervalCount: 1 }
}

const BillingTab = () => {
  const { activeOrganization, isOrgAdmin } = useOrganizationStore()
  const isPersonal = !activeOrganization

  const [loading, setLoading] = useState(true)
  const [subscription, setSubscription] = useState(null)
  const [stripeAvailable, setStripeAvailable] = useState(false)
  const [tiers, setTiers] = useState([])
  const [actionLoading, setActionLoading] = useState(false)
  const [orgData, setOrgData] = useState(null)
  const [notAuthorized, setNotAuthorized] = useState(false)
  const [billingCycle, setBillingCycle] = useState('monthly')
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showPlanChangeDialog, setShowPlanChangeDialog] = useState(false)
  const [selectedNewTier, setSelectedNewTier] = useState(null)
  const [billingReadOnly, setBillingReadOnly] = useState(false)

  // Load subscription data when org context changes
  const loadSubscriptionData = useCallback(async () => {
    setLoading(true)
    setNotAuthorized(false)

    try {
      // Check billing feature flags
      try {
        const flagsRes = await fetch('/api/subscription/feature-flags', { credentials: 'include' })
        if (flagsRes.ok) {
          const flagsData = await flagsRes.json()
          setBillingReadOnly(flagsData.readonly_billing === true)
        }
      } catch (flagError) {
        logger.error('Error checking billing flags:', flagError)
      }

      // Load tiers (public endpoint)
      const tiersRes = await subscriptionService.getAvailableTiers()
      setTiers(tiersRes.tiers || [])

      // Load subscription based on context
      if (isPersonal) {
        const subRes = await subscriptionService.getPersonalSubscription()
        setSubscription(subRes.subscription)
        setStripeAvailable(subRes.stripeAvailable)
        setOrgData(null)
      } else {
        try {
          const subRes = await subscriptionService.getOrganizationSubscription(
            activeOrganization.id
          )
          setSubscription(subRes.subscription)
          setStripeAvailable(subRes.stripeAvailable)
          setOrgData(subRes.organization)
        } catch (error) {
          if (error.response?.status === 403) {
            // User is not org admin - show read-only message
            setNotAuthorized(true)
            setSubscription(null)
          } else {
            throw error
          }
        }
      }
    } catch (error) {
      logger.error('Error loading subscription:', error)
      toast.error('Failed to load subscription information')
    } finally {
      setLoading(false)
    }
  }, [isPersonal, activeOrganization?.id])

  useEffect(() => {
    loadSubscriptionData()
  }, [loadSubscriptionData])

  const handleUpgrade = async (tierCode) => {
    setActionLoading(true)
    try {
      const result = isPersonal
        ? await subscriptionService.createCheckoutSession({ tier: tierCode, billingCycle })
        : await subscriptionService.createOrgCheckoutSession(activeOrganization.id, {
            tier: tierCode,
            billingCycle
          })

      if (result.url) {
        window.location.href = result.url
      }
    } catch (error) {
      logger.error('Error creating checkout:', error)
      toast.error(error.response?.data?.error || 'Failed to start upgrade process')
    } finally {
      setActionLoading(false)
    }
  }

  const handleManageBilling = async () => {
    setActionLoading(true)
    try {
      const returnUrl = `${window.location.origin}/profile#billing`
      const result = isPersonal
        ? await subscriptionService.createPortalSession(returnUrl)
        : await subscriptionService.createOrgPortalSession(activeOrganization.id, returnUrl)

      if (result.url) {
        window.location.href = result.url
      }
    } catch (error) {
      logger.error('Error opening billing portal:', error)
      toast.error(error.response?.data?.error || 'Failed to open billing portal')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCancelSubscription = async () => {
    setShowCancelDialog(false)
    setActionLoading(true)
    try {
      await subscriptionService.cancelSubscription()
      toast.success('Subscription will be cancelled at the end of your billing period')
      loadSubscriptionData()
    } catch (error) {
      logger.error('Error cancelling subscription:', error)
      toast.error(error.response?.data?.error || 'Failed to cancel subscription')
    } finally {
      setActionLoading(false)
    }
  }

  const handleReactivate = async () => {
    setActionLoading(true)
    try {
      await subscriptionService.reactivateSubscription()
      toast.success('Subscription reactivated!')
      loadSubscriptionData()
    } catch (error) {
      logger.error('Error reactivating subscription:', error)
      toast.error(error.response?.data?.error || 'Failed to reactivate subscription')
    } finally {
      setActionLoading(false)
    }
  }

  const handlePlanChange = async (tierCode) => {
    setActionLoading(true)
    try {
      const result = await subscriptionService.changePlan({ tierCode, billingCycle })
      const actionWord = result.isUpgrade ? 'upgraded' : 'changed'
      toast.success(`Successfully ${actionWord} to ${result.newTier}!`)
      setShowPlanChangeDialog(false)
      setSelectedNewTier(null)
      loadSubscriptionData()
    } catch (error) {
      logger.error('Error changing plan:', error)
      toast.error(error.response?.data?.error || 'Failed to change plan')
    } finally {
      setActionLoading(false)
    }
  }

  const initiatePlanChange = (tier) => {
    setSelectedNewTier(tier)
    setShowPlanChangeDialog(true)
  }

  // Format price from cents to dollars
  const formatPrice = (cents) => {
    return (cents / 100).toFixed(2)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // For org context, check if user can manage billing
  const canManageBilling = isPersonal || isOrgAdmin()
  const currentTier = subscription?.tier || { code: 'basic', name: 'Basic (Free)', features: {} }
  const isFreeTier = currentTier.code === 'basic'
  const isPendingCancel = subscription?.cancelAtPeriodEnd
  const discount = subscription?.stripeData?.discount

  // If user is not authorized to view org billing
  if (!isPersonal && notAuthorized) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {activeOrganization?.name} Billing
          </h3>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Admin Access Required
          </h4>
          <p className="text-gray-600 dark:text-gray-400">
            Contact your organization administrator to view or manage billing.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          {isPersonal ? 'Personal Billing' : `${activeOrganization?.name} Billing`}
        </h3>
        {!isPersonal && !canManageBilling && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Contact your organization admin to manage billing
          </span>
        )}
      </div>

      {/* Read-Only Billing Banner */}
      {billingReadOnly && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-primary-900 dark:text-primary-100">
                Billing changes are currently disabled
              </h4>
              <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                Your features are active during our beta period. Subscription management will be
                available soon.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current Plan Card */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-lg p-6 border border-indigo-200 dark:border-indigo-800">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                Current Plan
              </span>
            </div>
            <h4 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {currentTier.name}
            </h4>
            {currentTier.description && (
              <p className="text-gray-600 dark:text-gray-400 mt-1">{currentTier.description}</p>
            )}
          </div>

          {!isFreeTier && subscription?.currentPeriodEnd && (
            <div className="text-right">
              <div className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>
                  {isPendingCancel ? 'Ends' : 'Renews'}{' '}
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </span>
              </div>
              {isPendingCancel && (
                <span className="inline-flex items-center gap-1 px-2 py-1 mt-2 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                  <AlertTriangle className="w-3 h-3" />
                  Cancelling
                </span>
              )}
            </div>
          )}
        </div>

        {/* Active Discount Badge */}
        {discount && (
          <div className="mt-4 pt-4 border-t border-indigo-200 dark:border-indigo-700">
            <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <Tag className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-green-800 dark:text-green-200 truncate">
                  {discount.name}
                  {discount.promotionCode && (
                    <span className="ml-2 text-xs font-mono bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">
                      {discount.promotionCode}
                    </span>
                  )}
                </p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {discount.percentOff
                    ? `${discount.percentOff}% off`
                    : discount.amountOffCents
                      ? `$${(discount.amountOffCents / 100).toFixed(2)} off`
                      : 'Discount applied'}
                  {discount.duration === 'once' && ' (first payment)'}
                  {discount.duration === 'forever' && ' (forever)'}
                  {discount.duration === 'repeating' &&
                    discount.durationInMonths &&
                    ` (for ${discount.durationInMonths} months)`}
                  {discount.end && (
                    <span className="ml-2">
                      · Ends {new Date(discount.end).toLocaleDateString()}
                    </span>
                  )}
                </p>
              </div>
              <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            </div>
          </div>
        )}

        {/* Org member count */}
        {!isPersonal && orgData && (
          <div className="mt-4 pt-4 border-t border-indigo-200 dark:border-indigo-700">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Users className="w-4 h-4" />
              <span>
                {orgData.memberCount} member{orgData.memberCount !== 1 ? 's' : ''}
              </span>
              {subscription?.seatCount && subscription.seatCount > 1 && (
                <span className="text-gray-400">/ {subscription.seatCount} seats</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Features List */}
      {currentTier.features && Object.keys(currentTier.features).length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Plan Features</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(currentTier.features).map(([feature, enabled]) => (
              <div
                key={feature}
                className={`flex items-center gap-2 ${
                  enabled ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                <Check
                  className={`w-4 h-4 flex-shrink-0 ${
                    enabled ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'
                  }`}
                />
                <span className="text-sm capitalize">{feature.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Billing Actions - Hidden when billing is read-only */}
      {canManageBilling && stripeAvailable && !billingReadOnly && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Billing Management</h4>

          <div className="flex flex-wrap gap-3">
            {/* Manage Billing Portal */}
            {!isFreeTier && subscription?.stripeCustomerId && (
              <button
                onClick={handleManageBilling}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CreditCard className="w-4 h-4" />
                Manage Billing
                <ExternalLink className="w-3 h-3" />
              </button>
            )}

            {/* Reactivate if pending cancel */}
            {isPendingCancel && (
              <button
                onClick={handleReactivate}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reactivate Subscription
              </button>
            )}

            {/* Cancel option */}
            {!isFreeTier && !isPendingCancel && (
              <button
                onClick={() => setShowCancelDialog(true)}
                disabled={actionLoading}
                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium disabled:opacity-50"
              >
                Cancel Subscription
              </button>
            )}
          </div>
        </div>
      )}

      {/* Plan Options - Hidden when billing is read-only */}
      {canManageBilling && stripeAvailable && !billingReadOnly && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <h4 className="font-medium text-gray-900 dark:text-gray-100">
                {isFreeTier ? 'Upgrade Your Plan' : 'Available Plans'}
              </h4>
            </div>

            {/* Dynamic Billing Cycle Toggle */}
            {(() => {
              // Collect all unique interval keys from all tiers
              const allIntervalKeys = new Set(['monthly']) // Always include monthly as default
              tiers.forEach((tier) => {
                if (tier.stripePriceIds) {
                  Object.keys(tier.stripePriceIds).forEach((key) => {
                    if (tier.stripePriceIds[key]) allIntervalKeys.add(key)
                  })
                }
                // Also add annual if tier has annual price
                if (tier.annualPrice > 0) allIntervalKeys.add('annual')
              })

              // Sort intervals by duration (shortest first)
              const sortedKeys = Array.from(allIntervalKeys).sort((a, b) => {
                const aInfo = parseIntervalKey(a)
                const bInfo = parseIntervalKey(b)
                const aDuration =
                  aInfo.interval === 'year' ? aInfo.intervalCount * 12 : aInfo.intervalCount
                const bDuration =
                  bInfo.interval === 'year' ? bInfo.intervalCount * 12 : bInfo.intervalCount
                return aDuration - bDuration
              })

              // Calculate savings for each interval vs monthly (if monthly price exists)
              const intervalOptions = sortedKeys.map((key) => {
                const info = parseIntervalKey(key)
                const label = getIntervalLabel(info.interval, info.intervalCount)
                const durationMonths =
                  info.interval === 'year' ? info.intervalCount * 12 : info.intervalCount

                // Find a tier with this interval to calculate savings
                let savings = 0
                const tierWithInterval = tiers.find((t) => {
                  if (t.stripePriceIds?.[key] && t.monthlyPrice > 0) {
                    // Calculate what the interval price should be
                    // This is a rough estimate - actual price comes from tier
                    return true
                  }
                  return false
                })

                // Calculate savings based on annual vs monthly if available
                if (key === 'annual') {
                  const tierWithAnnual = tiers.find((t) => t.annualPrice > 0 && t.monthlyPrice > 0)
                  if (tierWithAnnual) {
                    savings = Math.round(
                      (1 - tierWithAnnual.annualPrice / (tierWithAnnual.monthlyPrice * 12)) * 100
                    )
                  }
                }

                return { key, label, durationMonths, savings }
              })

              return (
                <div className="flex flex-wrap items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 gap-0.5">
                  {intervalOptions.map((option) => (
                    <button
                      key={option.key}
                      onClick={() => setBillingCycle(option.key)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                        billingCycle === option.key
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                      }`}
                    >
                      {option.label}
                      {option.savings > 0 && (
                        <span className="ml-1 text-xs text-green-600 dark:text-green-400">
                          Save {option.savings}%
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )
            })()}
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {isFreeTier
              ? 'Unlock unlimited challenges, full analytics, and more features.'
              : 'Compare plans or switch to a different tier.'}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tiers
              .filter((t) => t.code !== 'basic' && t.monthlyPrice > 0)
              .map((tier) => {
                // Parse billing cycle to get interval info
                const cycleInfo = parseIntervalKey(billingCycle)
                const cycleDurationMonths =
                  cycleInfo.interval === 'year'
                    ? cycleInfo.intervalCount * 12
                    : cycleInfo.intervalCount

                // Get price for the selected billing cycle
                let displayPrice = tier.monthlyPrice
                let periodPrice = tier.monthlyPrice
                let hasPriceForCycle = true

                if (billingCycle === 'annual' && tier.annualPrice > 0) {
                  periodPrice = tier.annualPrice
                  displayPrice = tier.annualPrice / 12 // Monthly equivalent
                } else if (billingCycle !== 'monthly' && tier.stripePriceIds?.[billingCycle]) {
                  // Custom interval - estimate based on monthly if we don't have exact price
                  // The actual price will be used at checkout via stripePriceIds
                  const estimatedPrice = tier.monthlyPrice * cycleDurationMonths
                  periodPrice = estimatedPrice
                  displayPrice = estimatedPrice / cycleDurationMonths
                } else if (billingCycle !== 'monthly' && !tier.stripePriceIds?.[billingCycle]) {
                  // Tier doesn't have this billing cycle available
                  hasPriceForCycle = false
                }

                // Calculate savings vs monthly
                let savings = 0
                if (tier.monthlyPrice > 0 && cycleDurationMonths > 1 && hasPriceForCycle) {
                  const monthlyEquivalent = periodPrice / cycleDurationMonths
                  savings = Math.round((1 - monthlyEquivalent / tier.monthlyPrice) * 100)
                }
                // Use actual annual savings if available
                if (billingCycle === 'annual' && tier.annualPrice > 0 && tier.monthlyPrice > 0) {
                  savings = Math.round((1 - tier.annualPrice / (tier.monthlyPrice * 12)) * 100)
                }

                // Determine tier relationship to current plan
                const isCurrentTier = currentTier.code?.toLowerCase() === tier.code?.toLowerCase()
                const currentTierLevel = currentTier.tierLevel || 0
                const isUpgrade = tier.tierLevel > currentTierLevel
                const isDowngrade = tier.tierLevel < currentTierLevel

                // Detect current billing cycle from subscription period length
                let currentBillingCycle = 'monthly'
                if (subscription?.currentPeriodStart && subscription?.currentPeriodEnd) {
                  const periodDays = Math.round(
                    (new Date(subscription.currentPeriodEnd) -
                      new Date(subscription.currentPeriodStart)) /
                      (1000 * 60 * 60 * 24)
                  )
                  // Map period days to billing cycle key
                  if (periodDays > 330) currentBillingCycle = 'annual'
                  else if (periodDays > 280) currentBillingCycle = 'tenMonth'
                  else if (periodDays > 170) currentBillingCycle = 'sixMonth'
                  else if (periodDays > 80) currentBillingCycle = 'quarterly'
                }
                const isBillingCycleChange = isCurrentTier && billingCycle !== currentBillingCycle

                return (
                  <div
                    key={tier.id}
                    className={`relative border rounded-lg p-4 transition-colors ${
                      isCurrentTier
                        ? 'border-indigo-500 dark:border-indigo-400 bg-indigo-50/50 dark:bg-indigo-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                    }`}
                  >
                    {/* Current Plan Badge */}
                    {isCurrentTier && (
                      <div className="absolute -top-3 left-4">
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-indigo-600 text-white">
                          <Check className="w-3 h-3" />
                          Current Plan
                        </span>
                      </div>
                    )}

                    <h5
                      className={`font-semibold text-gray-900 dark:text-gray-100 ${isCurrentTier ? 'mt-2' : ''}`}
                    >
                      {tier.name}
                    </h5>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                      ${formatPrice(displayPrice)}
                      <span className="text-sm font-normal text-gray-500">/mo</span>
                    </p>
                    {/* Show billing details for selected cycle */}
                    {hasPriceForCycle && billingCycle !== 'monthly' && (
                      <p className="text-xs text-green-600 dark:text-green-400">
                        ${formatPrice(periodPrice)} billed{' '}
                        {getIntervalLabel(
                          cycleInfo.interval,
                          cycleInfo.intervalCount
                        ).toLowerCase()}
                        {savings > 0 && ` (save ${savings}%)`}
                      </p>
                    )}
                    {/* Show alternative pricing options */}
                    {billingCycle === 'monthly' && tier.annualPrice > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        or ${formatPrice(tier.annualPrice / 12)}/mo billed yearly
                      </p>
                    )}
                    {/* Show unavailable notice if tier doesn't support this interval */}
                    {!hasPriceForCycle && billingCycle !== 'monthly' && (
                      <div className="mt-1">
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          {getIntervalLabel(cycleInfo.interval, cycleInfo.intervalCount)} billing
                          not available
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Available:{' '}
                          {(() => {
                            const availableKeys = tier.stripePriceIds
                              ? Object.keys(tier.stripePriceIds).filter(
                                  (k) => tier.stripePriceIds[k]
                                )
                              : []
                            // Always include monthly if monthlyPrice exists
                            if (tier.monthlyPrice > 0 && !availableKeys.includes('monthly')) {
                              availableKeys.unshift('monthly')
                            }
                            // Include annual if annualPrice exists
                            if (tier.annualPrice > 0 && !availableKeys.includes('annual')) {
                              availableKeys.push('annual')
                            }
                            return availableKeys.length > 0
                              ? availableKeys
                                  .map((k) => {
                                    const info = parseIntervalKey(k)
                                    return getIntervalLabel(info.interval, info.intervalCount)
                                  })
                                  .join(', ')
                              : 'Monthly'
                          })()}
                        </p>
                      </div>
                    )}
                    {tier.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        {tier.description}
                      </p>
                    )}

                    {/* Action Button */}
                    {!hasPriceForCycle ? (
                      <button
                        disabled
                        className="w-full mt-4 px-4 py-2 rounded-md text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
                        title={`${tier.name} doesn't offer ${getIntervalLabel(cycleInfo.interval, cycleInfo.intervalCount)} billing`}
                      >
                        Select Different Interval
                      </button>
                    ) : isCurrentTier && !isBillingCycleChange ? (
                      <button
                        disabled
                        className="w-full mt-4 px-4 py-2 rounded-md text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
                      >
                        Current Plan
                      </button>
                    ) : isBillingCycleChange ? (
                      <button
                        onClick={() => initiatePlanChange(tier)}
                        disabled={actionLoading || isPendingCancel}
                        className="w-full mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 border border-indigo-300 dark:border-indigo-600 rounded-md shadow-sm text-sm font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Switch to {getIntervalLabel(cycleInfo.interval, cycleInfo.intervalCount)}
                      </button>
                    ) : isFreeTier ? (
                      <button
                        onClick={() => handleUpgrade(tier.code)}
                        disabled={actionLoading}
                        className="w-full mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Upgrade
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => initiatePlanChange(tier)}
                        disabled={actionLoading || isPendingCancel}
                        className={`w-full mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                          isUpgrade
                            ? 'border-transparent text-white bg-indigo-600 hover:bg-indigo-700'
                            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {isUpgrade ? 'Upgrade' : 'Downgrade'}
                        {isUpgrade && <ArrowRight className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                )
              })}
          </div>
        </div>
      )}

      {/* Stripe not configured notice */}
      {!stripeAvailable && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                Beta Mode Active
              </h4>
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                Payment processing is not currently configured. All users have access to Pro
                features during our beta period.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancelSubscription}
        title="Cancel Subscription?"
        message={`We're sorry to see you go! Your ${subscription?.tier?.name || 'current'} plan will remain active until ${subscription?.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'the end of your billing period'}. You can reactivate anytime before then.`}
        confirmText="Yes, Cancel"
        cancelText="Keep Subscription"
        icon={AlertTriangle}
      />

      {/* Plan Change Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showPlanChangeDialog}
        onClose={() => {
          setShowPlanChangeDialog(false)
          setSelectedNewTier(null)
        }}
        onConfirm={() => handlePlanChange(selectedNewTier?.code)}
        title={
          selectedNewTier?.code?.toLowerCase() === currentTier.code?.toLowerCase()
            ? `Switch to ${getIntervalLabel(parseIntervalKey(billingCycle).interval, parseIntervalKey(billingCycle).intervalCount)} Billing?`
            : `${selectedNewTier?.tierLevel > (currentTier.tierLevel || 0) ? 'Upgrade' : 'Change'} to ${selectedNewTier?.name}?`
        }
        message={
          selectedNewTier?.code?.toLowerCase() === currentTier.code?.toLowerCase()
            ? parseIntervalKey(billingCycle).intervalCount > 1 ||
              parseIntervalKey(billingCycle).interval === 'year'
              ? `You'll switch to ${getIntervalLabel(parseIntervalKey(billingCycle).interval, parseIntervalKey(billingCycle).intervalCount).toLowerCase()} billing. The change takes effect at your next billing date.`
              : "You'll switch to monthly billing. The change takes effect at your next billing date."
            : selectedNewTier?.tierLevel > (currentTier.tierLevel || 0)
              ? "You'll be charged a prorated amount for the upgrade. Your new plan will take effect immediately."
              : `Your plan will be changed to ${selectedNewTier?.name}. Any unused time on your current plan will be credited to your account.`
        }
        confirmText={
          selectedNewTier?.code?.toLowerCase() === currentTier.code?.toLowerCase()
            ? `Switch to ${getIntervalLabel(parseIntervalKey(billingCycle).interval, parseIntervalKey(billingCycle).intervalCount)}`
            : selectedNewTier?.tierLevel > (currentTier.tierLevel || 0)
              ? 'Upgrade Now'
              : 'Change Plan'
        }
        cancelText="Cancel"
        icon={selectedNewTier?.tierLevel > (currentTier.tierLevel || 0) ? Sparkles : AlertTriangle}
      />
    </div>
  )
}

export default BillingTab
